import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { DropdownProps, DropdownOption } from './types';
import './Dropdown.css';

export const Dropdown = <T extends string | number>({
  options,
  value,
  onChange,
  placeholder = 'Select option...',
  className = '',
  menuClassName = '',
  align = 'left',
  width = 'auto',
  disabled = false,
  showChevron = true,
  hideCheckmark = false,
  ariaLabel,
  onOpenChange,
}: DropdownProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value),
    [options, value]
  );

  const toggleOpen = useCallback(() => {
    if (disabled) return;
    setIsOpen((prev) => {
      const next = !prev;
      onOpenChange?.(next);
      return next;
    });
  }, [disabled, onOpenChange]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    onOpenChange?.(false);
    setHighlightedIndex(-1);
  }, [onOpenChange]);

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (e: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isOpen, closeDropdown]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        onOpenChange?.(true);
        const idx = options.findIndex((opt) => opt.value === value);
        setHighlightedIndex(idx >= 0 ? idx : 0);
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
      case 'Tab':
        closeDropdown();
        triggerRef.current?.focus();
        break;

      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => {
          let next = prev + 1;
          if (next >= options.length) next = 0;
          while (options[next]?.disabled && next !== prev) {
            next = (next + 1) % options.length;
          }
          return next;
        });
        break;

      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => {
          let next = prev - 1;
          if (next < 0) next = options.length - 1;
          while (options[next]?.disabled && next !== prev) {
            next = (next - 1 + options.length) % options.length;
          }
          return next;
        });
        break;

      case 'Enter':
      case ' ':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < options.length) {
          const opt = options[highlightedIndex];
          if (!opt.disabled) {
            onChange(opt.value);
            closeDropdown();
            triggerRef.current?.focus();
          }
        }
        break;
    }
  };

  const handleOptionClick = (opt: DropdownOption<T>) => {
    if (opt.disabled) return;
    onChange(opt.value);
    closeDropdown();
    triggerRef.current?.focus();
  };

  const menuStyle = useMemo<React.CSSProperties>(() => {
    if (width === 'trigger') {
      return { width: '100%' };
    }
    if (width !== 'auto') {
      return { width };
    }
    return {};
  }, [width]);

  return (
    <div
      ref={containerRef}
      className={`custom-dropdown-container ${className}`}
      onKeyDown={handleKeyDown}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`custom-dropdown-trigger ${isOpen ? 'is-open' : ''}`}
        onClick={toggleOpen}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
      >
        <span className="custom-dropdown-trigger-content">
          {selectedOption?.icon && (
            <span className="custom-dropdown-icon">{selectedOption.icon}</span>
          )}
          <span className="custom-dropdown-label">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </span>

        {showChevron && (
          <span className="custom-dropdown-chevron" aria-hidden="true">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        )}
      </button>

      {isOpen && (
        <ul
          ref={menuRef}
          className={`custom-dropdown-menu align-${align} ${menuClassName}`}
          style={menuStyle}
          role="listbox"
          aria-label={ariaLabel}
        >
          {options.map((opt, idx) => {
            const isSelected = opt.value === value;
            const isHighlighted = idx === highlightedIndex;

            return (
              <li
                key={String(opt.value)}
                role="option"
                aria-selected={isSelected}
                aria-disabled={opt.disabled}
                className={`custom-dropdown-item ${isSelected ? 'is-selected' : ''} ${
                  isHighlighted ? 'is-highlighted' : ''
                } ${opt.disabled ? 'is-disabled' : ''} ${opt.className || ''}`}
                onClick={() => handleOptionClick(opt)}
                onMouseEnter={() => setHighlightedIndex(idx)}
              >
                <div className="custom-dropdown-item-content">
                  {opt.icon && (
                    <span className="custom-dropdown-icon">{opt.icon}</span>
                  )}
                  <span className="custom-dropdown-item-label">{opt.label}</span>
                </div>

                {!hideCheckmark && isSelected && (
                  <span className="custom-dropdown-checkmark" aria-hidden="true">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
